import React, { useState } from 'react';
import Icon from '../AppIcon';

const SessionStatusIndicator = ({ 
  status, 
  capacity, 
  registered, 
  paymentStatus,
  registrationPhase,
  showTooltip = true 
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const getStatusConfig = () => {
    switch (status) {
      case 'open':
        return {
          label: '등록 가능',
          color: 'bg-success',
          textColor: 'text-white',
          icon: 'CheckCircle',
          description: 'Registration is open'
        };
      case 'full':
        return {
          label: '정원 마감',
          color: 'bg-warning',
          textColor: 'text-white',
          icon: 'AlertCircle',
          description: 'Session is at full capacity'
        };
      case 'closed':
        return {
          label: '등록 마감',
          color: 'bg-error',
          textColor: 'text-white',
          icon: 'XCircle',
          description: 'Registration is closed'
        };
      case 'cancelled':
        return {
          label: '취소됨',
          color: 'bg-destructive',
          textColor: 'text-white',
          icon: 'Ban',
          description: 'Session has been cancelled'
        };
      case 'completed':
        return {
          label: '완료됨',
          color: 'bg-muted',
          textColor: 'text-primary',
          icon: 'Check',
          description: 'Session has been completed'
        };
      default:
        return {
          label: '대기',
          color: 'bg-secondary',
          textColor: 'text-white',
          icon: 'Clock',
          description: 'Session is pending'
        };
    }
  };

  const getPaymentStatusConfig = () => {
    if (!paymentStatus) return null;
    
    switch (paymentStatus) {
      case 'paid':
        return {
          label: '결제 완료',
          color: 'bg-success',
          textColor: 'text-white',
          icon: 'DollarSign'
        };
      case 'pending':
        return {
          label: '결제 대기',
          color: 'bg-warning',
          textColor: 'text-white',
          icon: 'Clock'
        };
      case 'overdue':
        return {
          label: '미결제',
          color: 'bg-error',
          textColor: 'text-white',
          icon: 'AlertTriangle'
        };
      default:
        return null;
    }
  };

  const getRegistrationPhaseConfig = () => {
    if (!registrationPhase) return null;
    
    switch (registrationPhase) {
      case 'early':
        return {
          label: '얼리',
          color: 'bg-accent',
          textColor: 'text-white',
          icon: 'Zap'
        };
      case 'regular':
        return {
          label: '일반',
          color: 'bg-primary',
          textColor: 'text-white',
          icon: 'Calendar'
        };
      case 'late':
        return {
          label: '마감 임박',
          color: 'bg-warning',
          textColor: 'text-white',
          icon: 'Clock'
        };
      default:
        return null;
    }
  };

  const statusConfig = getStatusConfig();
  const paymentConfig = getPaymentStatusConfig();
  const phaseConfig = getRegistrationPhaseConfig();

  const isCompleted = status === 'completed';

  const capacityPercentage = capacity ? (registered / capacity) * 100 : 0;
  const isNearCapacity = capacityPercentage >= 80;

  const handleMouseEnter = () => {
    if (showTooltip) {
      setIsTooltipVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setIsTooltipVisible(false);
  };

  const handleTouchStart = () => {
    if (showTooltip) {
      setIsTooltipVisible(!isTooltipVisible);
    }
  };

  return (
    <div className="relative w-full">
      <div
        className="flex flex-nowrap items-center gap-2 overflow-x-auto"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
      >
        <div className={`
          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
          ${isCompleted ? 'bg-primary/10' : statusConfig?.color}
          ${isCompleted ? 'border border-primary/20' : 'border border-transparent'}
          transition-athletic
          whitespace-nowrap
        `}>
          <Icon 
            name={statusConfig?.icon} 
            size={16} 
            color={isCompleted ? 'var(--color-primary)' : 'white'}
          />
          <span className={`text-xs font-medium ${statusConfig?.textColor}`}>
            {statusConfig?.label}
          </span>
        </div>

        {capacity && (
          <div className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
            ${isNearCapacity ? 'bg-warning' : 'bg-muted-foreground'}
            border border-transparent
            transition-athletic
            whitespace-nowrap
          `}>
            <Icon 
              name="Users" 
              size={16} 
              color="white"
            />
            <span className="text-xs font-medium data-text text-white">
              {registered}/{capacity}
            </span>
          </div>
        )}

        {paymentConfig && (
          <div className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
            ${paymentConfig?.color}
            border border-transparent
            transition-athletic
            whitespace-nowrap
          `}>
            <Icon 
              name={paymentConfig?.icon} 
              size={16}
              color="white"
            />
            <span className={`text-xs font-medium ${paymentConfig?.textColor}`}>
              {paymentConfig?.label}
            </span>
          </div>
        )}

        {phaseConfig && (
          <div className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
            ${phaseConfig?.color}
            border border-transparent
            transition-athletic
            whitespace-nowrap
          `}>
            <Icon 
              name={phaseConfig?.icon} 
              size={16}
              color="white"
            />
            <span className={`text-xs font-medium ${phaseConfig?.textColor}`}>
              {phaseConfig?.label}
            </span>
          </div>
        )}
      </div>
      {showTooltip && isTooltipVisible && (
        <div className="
          absolute top-full left-0 mt-2 z-50
          bg-popover text-popover-foreground
          px-4 py-3 rounded-md shadow-lg
          min-w-64 max-w-sm
          border border-border
        ">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icon name={statusConfig?.icon} size={16} />
              <span className="font-medium">{statusConfig?.label}</span>
            </div>
            <p className="text-sm caption text-muted-foreground">
              {statusConfig?.description}
            </p>
            
            {capacity && (
              <div className="pt-2 border-t border-border">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm caption">Capacity</span>
                  <span className="text-sm font-medium data-text">
                    {capacityPercentage?.toFixed(0)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-athletic ${
                      isNearCapacity ? 'bg-warning' : 'bg-success'
                    }`}
                    style={{ width: `${capacityPercentage}%` }}
                  />
                </div>
              </div>
            )}

            {paymentConfig && (
              <div className="pt-2 border-t border-border">
                <div className="flex items-center gap-2">
                  <Icon name={paymentConfig?.icon} size={14} />
                  <span className="text-sm caption">{paymentConfig?.label}</span>
                </div>
              </div>
            )}

            {phaseConfig && (
              <div className="pt-2 border-t border-border">
                <div className="flex items-center gap-2">
                  <Icon name={phaseConfig?.icon} size={14} />
                  <span className="text-sm caption">{phaseConfig?.label}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionStatusIndicator;